class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));

    this.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-cart-discount-form]');
      if (!form) return;
      event.preventDefault();
      this.applyDiscount(form);
    });

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.drawerBound === 'true') return;

    cartLink.dataset.drawerBound = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
      if (!summary.hasAttribute('role')) this.setSummaryAccessibility(summary);
    });

    requestAnimationFrame(() => this.classList.add('animate', 'active'));

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(summary) {
    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-expanded', summary.closest('details')?.hasAttribute('open') ? 'true' : 'false');

    const content = summary.nextElementSibling;
    if (content?.id) summary.setAttribute('aria-controls', content.id);

    summary.addEventListener('click', (event) => {
      const details = event.currentTarget.closest('details');
      event.currentTarget.setAttribute('aria-expanded', details?.hasAttribute('open') ? 'false' : 'true');
    });

    summary.parentElement?.addEventListener('keyup', onKeyUpEscape);
  }

  async applyDiscount(form) {
    const input = form.querySelector('input[name="discount"]');
    const button = form.querySelector('button[type="submit"]');
    const status = form.querySelector('[data-cart-discount-status]');
    const spinner = button?.querySelector('.loading-overlay__spinner');
    const codes = [
      ...new Set(
        (input?.value || '')
          .split(',')
          .map((code) => code.trim())
          .filter(Boolean)
      )
    ];
    const code = codes.join(',');

    if (!code) {
      if (status) {
        status.textContent = 'Enter at least one discount code.';
        status.classList.add('is-error');
      }
      input?.focus();
      return;
    }

    if (input) input.value = code;

    button?.setAttribute('disabled', 'disabled');
    button?.classList.add('loading');
    spinner?.classList.remove('hidden');
    if (status) {
      status.textContent = 'Applying discount…';
      status.classList.remove('is-error');
    }

    try {
      const response = await fetch(routes.cart_update_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          discount: code,
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname
        })
      });

      const state = await response.json();
      if (!response.ok || state.status) {
        throw new Error(state.description || state.message || 'This discount code could not be applied.');
      }

      if (state.sections) {
        this.renderContents(state);
      } else {
        await this.refreshSections();
      }

      setTimeout(() => {
        const refreshedStatus = this.querySelector('[data-cart-discount-status]');
        if (refreshedStatus) {
          refreshedStatus.textContent =
            codes.length === 1
              ? 'Discount code updated. Applied savings appear below.'
              : 'Discount codes updated. Applied savings appear below.';
          refreshedStatus.classList.remove('is-error');
        }
      }, 80);

      document.dispatchEvent(new CustomEvent('shopify:cart:discount-update', {
        bubbles: true,
        detail: { discountCodes: codes.map((discountCode) => ({ code: discountCode })) }
      }));
    } catch (error) {
      if (status) {
        status.textContent = error.message || 'This discount code could not be applied.';
        status.classList.add('is-error');
      }
    } finally {
      button?.removeAttribute('disabled');
      button?.classList.remove('loading');
      spinner?.classList.add('hidden');
    }
  }

  async refreshSections() {
    const sectionIds = this.getSectionsToRender().map((section) => section.id).join(',');
    const separator = window.location.search ? '&' : '?';
    const response = await fetch(`${window.location.pathname}${window.location.search}${separator}sections=${sectionIds}`);
    if (!response.ok) throw new Error('Cart could not be refreshed.');
    const sections = await response.json();
    this.renderContents({ sections });
  }

  renderContents(parsedState) {
    const inner = this.querySelector('.drawer__inner');
    if (inner?.classList.contains('is-empty')) inner.classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionElement || !sectionHtml) return;

      const replacement = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (replacement !== null) sectionElement.innerHTML = replacement;
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const element = documentFragment.querySelector(selector);
    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
